export type AppThemeId =
  | 'arcane'
  | 'obsidian'
  | 'candlelight'
  | 'feywild'
  | 'crimson-night'
  | 'forge';

export type AppThemeLaunchTier = 'launch' | 'phase2';

export type AppThemeDefinition = {
  id: AppThemeId;
  label: string;
  emoji: string;
  description: string;
  launchTier: AppThemeLaunchTier;
  /** Mini preview swatches for picker card */
  preview: {
    background: string;
    primary: string;
    accent: string;
  };
  themeColorMeta: string;
};

export const DEFAULT_APP_THEME: AppThemeId = 'arcane';

export const APP_THEMES: AppThemeDefinition[] = [
  {
    id: 'arcane',
    label: 'Arcane',
    emoji: '✨',
    description: 'Magical operating system — Mythkit brand default',
    launchTier: 'launch',
    preview: { background: '#090a0f', primary: 'oklch(62% 0.24 300)', accent: 'oklch(78% 0.14 90)' },
    themeColorMeta: '#090a0f',
  },
  {
    id: 'obsidian',
    label: 'Obsidian',
    emoji: '⚫',
    description: 'Mission control — minimal, professional',
    launchTier: 'launch',
    preview: { background: '#050506', primary: 'oklch(72% 0.03 260)', accent: 'oklch(82% 0.04 90)' },
    themeColorMeta: '#050506',
  },
  {
    id: 'candlelight',
    label: 'Candlelight',
    emoji: '🕯',
    description: 'Warm tabletop — tavern after dark',
    launchTier: 'launch',
    preview: { background: '#1a1410', primary: 'oklch(72% 0.14 75)', accent: 'oklch(72% 0.12 55)' },
    themeColorMeta: '#1a1410',
  },
  {
    id: 'feywild',
    label: 'Feywild',
    emoji: '🌿',
    description: 'Bioluminescent wonder — exploration and magic',
    launchTier: 'launch',
    preview: { background: '#061816', primary: 'oklch(68% 0.16 155)', accent: 'oklch(72% 0.12 300)' },
    themeColorMeta: '#061816',
  },
  {
    id: 'crimson-night',
    label: 'Crimson Night',
    emoji: '🌙',
    description: 'Gothic horror — moonlight and torchfire',
    launchTier: 'phase2',
    preview: { background: '#12080c', primary: 'oklch(48% 0.18 15)', accent: 'oklch(78% 0.08 90)' },
    themeColorMeta: '#12080c',
  },
  {
    id: 'forge',
    label: 'Forge',
    emoji: '⚔️',
    description: 'Dwarven workshop — steel and brass',
    launchTier: 'phase2',
    preview: { background: '#0c1014', primary: 'oklch(62% 0.1 240)', accent: 'oklch(72% 0.12 70)' },
    themeColorMeta: '#0c1014',
  },
];

const THEME_IDS = new Set(APP_THEMES.map((t) => t.id));

export function isAppThemeId(value: string | null | undefined): value is AppThemeId {
  return !!value && THEME_IDS.has(value as AppThemeId);
}

export function getAppTheme(id: AppThemeId): AppThemeDefinition {
  return APP_THEMES.find((t) => t.id === id) ?? APP_THEMES[0];
}

export function getLaunchThemes(): AppThemeDefinition[] {
  return APP_THEMES.filter((t) => t.launchTier === 'launch');
}

/** All themes available in the settings picker. */
export function getSelectableThemes(): AppThemeDefinition[] {
  return APP_THEMES;
}

/** Map saved board layout theme ids to current app theme ids */
export function normalizeLegacyThemeId(value: string | null | undefined): AppThemeId {
  if (!value) return DEFAULT_APP_THEME;
  if (value === 'arcane-dark') return 'arcane';
  if (isAppThemeId(value)) return value;
  return DEFAULT_APP_THEME;
}
